import type { AlAdvancedAction, AlCategory, AlSimpleDay, AlSimpleGroup, AlStatus } from '@elva/ui';
import { growthGet, growthGetBlob, growthGetWithMeta } from '@/data/http';
import { downloadBlob } from '@/data/download';
import type {
  BeAutopilotAction,
  BeAutopilotListPayload,
  BeAutopilotTimeline,
  BeTimelineDay,
} from '@/data/marketingApiTypes';
import { enumLower, num, relativeTimeFromIso, str } from '@/data/adapt';
import { autopilotCategoryToFe, autopilotStatusToFe } from '@/data/growthEnums';

export interface AutopilotLogFilters {
  category?: string;
  impact_category?: string;
  status?: string;
  action_type?: string;
  page?: number;
  limit?: number;
  days?: number;
}

export interface AutopilotLogData {
  simpleTimeline: AlSimpleDay[];
  advancedActions: AlAdvancedAction[];
  totalActions: number;
  categoryCounts: Record<AlCategory | 'all', number>;
}

const AL_CATEGORIES: AlCategory[] = [
  'ads',
  'landing',
  'seo',
  'gbp',
  'intel',
  'technical',
  'call',
];

const CATEGORY_LABELS: Record<AlCategory, string> = {
  ads: 'Ads',
  landing: 'Landing Pages',
  seo: 'SEO',
  gbp: 'GBP',
  intel: 'Intelligence',
  technical: 'Technical',
  call: 'Calls',
};

function emptyCategoryCounts(): Record<AlCategory | 'all', number> {
  const counts = { all: 0 } as Record<AlCategory | 'all', number>;
  for (const cat of AL_CATEGORIES) counts[cat] = 0;
  return counts;
}

function adaptCategoryCounts(counts: Record<string, number> | undefined): Record<AlCategory | 'all', number> {
  const result = emptyCategoryCounts();
  if (!counts) return result;
  for (const [key, value] of Object.entries(counts)) {
    const fe = autopilotCategoryToFe(key);
    result[fe] = num(result[fe]) + num(value);
    result.all += num(value);
  }
  return result;
}

function formatAutopilotPayload(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value.trim() || '—';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '—';
    if (entries.length === 1) {
      const first = entries[0];
      if (!first) return '—';
      const [key, nested] = first;
      const formatted = formatAutopilotPayload(nested);
      return formatted === '—' ? `${key}: —` : `${key}: ${formatted}`;
    }
    return entries.map(([key, nested]) => `${key}: ${formatAutopilotPayload(nested)}`).join(' · ');
  }
  return String(value);
}

function formatAutopilotLabel(value: string): string {
  if (!value || value === '—') return '—';
  return value.replace(/_/g, ' ');
}

function formatAutopilotEntity(entity: string): string {
  if (!entity || entity === '—') return '—';
  const match = entity.match(/^([a-z0-9_]+)\s*-\s*(\d+)$/i);
  if (match?.[1] && match[2]) {
    const label = match[1].replace(/_/g, ' ');
    return `${label.charAt(0).toUpperCase()}${label.slice(1)} #${match[2]}`;
  }
  return entity;
}

function formatAutopilotTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return str(iso);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (!Number.isFinite(date.getTime())) return dateStr;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dayLabelFromDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (!Number.isFinite(date.getTime())) return 'Day';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - day.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'long' });
}

function adaptAdvanced(row: BeAutopilotAction): AlAdvancedAction {
  const timestamp = row.timestamp ?? row.attempted_at;
  return {
    id: str(row.id),
    timestamp: formatAutopilotTimestamp(timestamp),
    category: autopilotCategoryToFe(row.category),
    action: formatAutopilotLabel(str(row.action ?? row.action_type)),
    entity: formatAutopilotEntity(str(row.entity, '—')),
    detail: str(row.description ?? row.detail, '—'),
    before: formatAutopilotPayload(row.before),
    after: formatAutopilotPayload(row.after),
    status: autopilotStatusToFe(row.status),
    trigger: formatAutopilotLabel(str(row.trigger, 'Autopilot')),
  };
}

function normalizeTimelineCategories(
  categories: BeTimelineDay['categories'],
): Array<{ category: string; count: number }> {
  if (!categories) return [];
  if (Array.isArray(categories)) {
    return categories.map((row) => ({
      category: str(row.category),
      count: num(row.count ?? row.action_count),
    }));
  }
  return Object.entries(categories).map(([category, count]) => ({ category, count: num(count) }));
}

function adaptTimelineGroup(row: Record<string, unknown>, index: number, date: string): AlSimpleGroup {
  const category = autopilotCategoryToFe(row.category as string);
  return {
    id: str(row.id, `${date}-g-${index}`),
    category,
    title: str(row.title, `${CATEGORY_LABELS[category]} activity`),
    outcome: str(row.outcome),
    detail: str(row.detail),
    time: str(row.time ?? relativeTimeFromIso(row.timestamp as string), 'Throughout the day'),
    actionCount: num(row.actionCount ?? row.action_count, 1),
  };
}

function groupsFromCategories(date: string, categories: BeTimelineDay['categories']): AlSimpleGroup[] {
  return normalizeTimelineCategories(categories)
    .filter((row) => row.count > 0)
    .map((row, index) => {
      const category = autopilotCategoryToFe(row.category);
      const label = CATEGORY_LABELS[category];
      const count = row.count;
      return {
        id: `${date}-${row.category}-${index}`,
        category,
        title: `${count} ${label} action${count === 1 ? '' : 's'}`,
        outcome: '',
        detail: '',
        time: 'Throughout the day',
        actionCount: count,
      };
    });
}

function adaptTimelineDay(row: BeTimelineDay): AlSimpleDay {
  const dateRaw = str(row.date);
  const wireGroups = (row.groups ?? []).map((group, index) => adaptTimelineGroup(group, index, dateRaw));
  const groups = wireGroups.length > 0 ? wireGroups : groupsFromCategories(dateRaw, row.categories);
  const actionCount = num(
    row.total ?? row.action_count,
    groups.reduce((sum, group) => sum + group.actionCount, 0),
  );

  return {
    dayLabel: str(row.day_label, dayLabelFromDate(dateRaw)),
    date: formatDisplayDate(dateRaw),
    actionCount,
    groups,
  };
}

function buildSimpleTimelineFromActions(rows: BeAutopilotAction[]): AlSimpleDay[] {
  const byDate = new Map<string, BeAutopilotAction[]>();
  for (const row of rows) {
    const iso = row.timestamp ?? row.attempted_at;
    const dateKey = iso?.slice(0, 10) ?? 'unknown';
    const list = byDate.get(dateKey) ?? [];
    list.push(row);
    byDate.set(dateKey, list);
  }

  return [...byDate.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dateKey, dayRows]) => {
      const byCategory = new Map<AlCategory, BeAutopilotAction[]>();
      for (const row of dayRows) {
        const category = autopilotCategoryToFe(row.category);
        const list = byCategory.get(category) ?? [];
        list.push(row);
        byCategory.set(category, list);
      }

      const groups: AlSimpleGroup[] = [...byCategory.entries()].map(([category, catRows], index) => {
        const detail = str(catRows[0]?.description ?? catRows[0]?.detail, '—');
        const count = catRows.length;
        return {
          id: `${dateKey}-${category}-${index}`,
          category,
          title:
            count === 1 && detail !== '—'
              ? detail
              : `${count} ${CATEGORY_LABELS[category]} action${count === 1 ? '' : 's'}`,
          outcome: count === 1 && catRows[0]?.status === 'success' ? 'Completed successfully' : '',
          detail:
            count > 1
              ? catRows.map((row) => str(row.description ?? row.detail, '—')).join(' · ')
              : '',
          time:
            count === 1 && catRows[0]?.timestamp
              ? relativeTimeFromIso(catRows[0].timestamp)
              : 'Throughout the day',
          actionCount: count,
        };
      });

      return {
        dayLabel: dayLabelFromDate(dateKey),
        date: formatDisplayDate(dateKey),
        actionCount: dayRows.length,
        groups,
      };
    });
}

async function fetchAutopilotList(
  filters: AutopilotLogFilters,
): Promise<{ items: BeAutopilotAction[]; counts?: Record<string, number>; totalCount?: number }> {
  const listParams: Record<string, string | number> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 100,
  };
  if (filters.category) listParams.category = filters.category;
  if (filters.impact_category) listParams.impact_category = filters.impact_category;
  if (filters.status) listParams.status = filters.status;
  if (filters.action_type) listParams.action_type = filters.action_type;

  const { data, meta } = await growthGetWithMeta<BeAutopilotListPayload | BeAutopilotAction[]>(
    '/autopilot-log/',
    listParams,
  );

  if (Array.isArray(data)) {
    return {
      items: data,
      ...(meta?.total_count != null ? { totalCount: meta.total_count } : {}),
    };
  }

  const totalCount = data.meta?.total_count ?? meta?.total_count;
  return {
    items: data.items ?? [],
    ...(data.counts_by_category ? { counts: data.counts_by_category } : {}),
    ...(totalCount != null ? { totalCount } : {}),
  };
}

export async function fetchAutopilotLog(
  filters: AutopilotLogFilters = {},
): Promise<AutopilotLogData> {
  const days = Math.min(30, Math.max(1, filters.days ?? 7));
  const countFilters: AutopilotLogFilters = { page: 1, limit: 1, days };

  const [listResult, countsResult, timelineResult] = await Promise.allSettled([
    fetchAutopilotList(filters),
    fetchAutopilotList(countFilters),
    growthGet<BeAutopilotTimeline | BeTimelineDay[]>('/autopilot-log/timeline/', { days }),
  ]);

  if (listResult.status === 'rejected') throw listResult.reason;

  const advancedActions = listResult.value.items.map(adaptAdvanced);
  const categoryCounts = adaptCategoryCounts(
    countsResult.status === 'fulfilled' ? countsResult.value.counts : listResult.value.counts,
  );

  const timeline =
    timelineResult.status === 'fulfilled' ? timelineResult.value : ([] as BeTimelineDay[]);
  const daysRows = Array.isArray(timeline)
    ? timeline
    : ((timeline as BeAutopilotTimeline).days ?? []);
  let simpleTimeline = daysRows.map(adaptTimelineDay);
  if (simpleTimeline.length === 0 && listResult.value.items.length > 0) {
    simpleTimeline = buildSimpleTimelineFromActions(listResult.value.items);
  }

  const timelineTotal = Array.isArray(timeline)
    ? undefined
    : (timeline as BeAutopilotTimeline).total_actions;
  const totalActions =
    num(timelineTotal) ||
    categoryCounts.all ||
    num(listResult.value.totalCount) ||
    simpleTimeline.reduce((sum, day) => sum + day.actionCount, 0) ||
    advancedActions.length;

  return { simpleTimeline, advancedActions, totalActions, categoryCounts };
}

export async function exportAutopilotLogCsv(): Promise<void> {
  const blob = await growthGetBlob('/autopilot-log/export/', { format: 'csv' });
  downloadBlob(blob, 'autopilot-log.csv');
}

export function autopilotStatusToBe(status: AlStatus | 'all'): string | undefined {
  if (status === 'all') return undefined;
  return enumLower(status, status);
}

export function autopilotCategoryToBe(category: AlCategory | 'all'): string | undefined {
  if (category === 'all') return undefined;
  return category;
}
